import * as THREE from 'three';
import { TimelineMax, TweenMax, Expo } from 'gsap/TweenMax';
import _debounce from 'lodash/debounce';
import noise from '@/scripts/utils/perlinNoise';
import createEvent from '@/scripts/utils/create-event';
import { DOCUMENT_ELEMENT } from '@/scripts/constants';
import ScrollMagic from 'scrollmagic';
import Controller from '@/scripts/utils/scrollmagic/controller';
// const OrbitControls = require('three-orbit-controls')(THREE);
import vertex from './vertex.glsl';
import fragment from './fragment.glsl';

const defaults = {
  element: null,
  container: null,
  initCallback: () => {},
};

export default class ParticlesPreloader {
  constructor(props) {
    const options = Object.assign({}, defaults, props);
    this.element = options.element;
    this.container = options.container;
    this.initCallback = options.initCallback;
    if (!this.element) return;

    this.dpi = window.devicePixelRatio;
    this.vw = $(this.element).width();
    this.vh = $(this.element).height();
    this.aspect = 1;

    // THEE init
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      70,
      this.vw / this.vh,
      0.001, 100,
    );
    this.camera.position.z = 1;

    this.scene.add(this.camera);

    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
    });

    this.geometry = new THREE.BufferGeometry();
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        time: { type: 'f', value: 1 },
        progress: { type: 'f', value: 1 },
      },
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
    });
    this.particles = null;
    this.scrollProgress = 0;
    this.render = this.render.bind(this);
    this.resize = this.resize.bind(this);

    this.resize_debounced = _debounce(this.resize, 100);

    this.init();
  }

  get imageData() {
    return new Promise((resolve) => {
      const { src } = this.element.dataset;
      const image = new Image();
      image.addEventListener('load', () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const { width, height } = image;
        this.aspect = width / height;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(image, 0, 0);
        resolve(ctx.getImageData(0, 0, width, height));
      });
      image.src = src;
    });
  }

  async drawImage() {
    const imageData = await this.imageData;
    const positions = [];
    const noisePos = [];
    const colors = [];
    const { data, width, height } = imageData;
    let i = 0;
    let j = 0;
    for (let x = 0; x < width; x += 2) {
      i += 0.01;
      for (let y = 0; y < height; y += 2) {
        j += 0.01;
        const coord = (x * 4 + y * 4 * width);
        const point = {
          r: data[coord] / 255,
          g: data[coord + 1] / 255,
          b: data[coord + 2] / 255,
          a: data[coord + 3] / 255,
        };
        if (point.a > 0) {
          positions.push((x - width / 2) / width, (-y + height / 2) / height, point.a);
          noisePos.push(
            2 - noise(Math.sin(i), Math.cos(j), i + j / 2) * 4,
            4 - noise(Math.sin(j), Math.cos(i), j + i / 2) * 8,
            2 - noise(Math.sin(j), Math.cos(i), j + i / 2) * 4,
          );
          colors.push(point.r, point.g, point.b, point.a * 4);
        }
      }
    }
    this.geometry.addAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    this.geometry.addAttribute('noise', new THREE.Float32BufferAttribute(noisePos, 3));
    this.geometry.addAttribute('color', new THREE.Float32BufferAttribute(colors, 4));
    this.particles = new THREE.Points(this.geometry, this.material);
    this.particles.position.y = -0.05;
    this.scene.add(this.particles);
    requestAnimationFrame(() => {
      this.resize();
      window.addEventListener('resize', this.resize_debounced);
    });

    requestIdleCallback(() => {
      TweenMax.ticker.addEventListener('tick', this.render);
      setTimeout(() => {
        this.initCallback();
      }, 1000);
    }, { timeout: 1000 });
  }

  play() {
    const tl = new TimelineMax({
      onComplete: () => {
        TweenMax.ticker.addEventListener('tick', () => {
          this.material.uniforms.progress.value += (this.scrollProgress * 2 - this.material.uniforms.progress.value) * 0.03;
        });
        DOCUMENT_ELEMENT.dispatchEvent(createEvent('historyReady'));
      },
    });
    tl
      .to(this.material.uniforms.progress, 3, {
        value: 0,
        ease: Expo.easeOut,
      });
  }

  playPreloader(callback = () => {}) {
    const tl = new TimelineMax();


    tl
      .to(this.material.uniforms.progress, 3, {
        value: 0,
        ease: Expo.easeOut,
        onComplete: () => callback(),
      })
      .to(this.material.uniforms.progress, 3, {
        value: 1,
        ease: Expo.easeOut,
      });
  }

  destroy() {
    TweenMax.ticker.removeEventListener('tick', this.render);
    window.removeEventListener('resize', this.resize_debounced);

    this.scene.remove(this.particles);
    if (this.renderer.context.getExtension('WEBGL_lose_context')) {
      this.renderer.context.getExtension('WEBGL_lose_context').loseContext();
    }
    this.geometry.dispose();
    this.material.dispose();
  }

  render() {
    this.material.uniforms.time.value += 0.01;
    this.renderer.render(this.scene, this.camera);
  }

  scroll() {
    if (!this.container) return;
    const scene = new ScrollMagic.Scene({
      triggerHook: 'onLeave',
      triggerElement: this.container,
      duration: this.container.offsetHeight,
      offset: 200,
    })
      .on('progress', (e) => {
        this.scrollProgress = e.progress;
      })
      .addTo(Controller);

    window.addEventListener('resize', _debounce(() => {
      scene.duration(this.container.offsetHeight);
    }, 100));
  }

  resize() {
    const width = $(this.element).width();
    // if (width !== this.vw) {
    //   this.container.style.paddingTop = `${this.vh - 150}px`;
    // }
    this.vw = width;
    this.vh = $(this.element).height();
    this.renderer.setSize(this.vw, this.vh);
    this.camera.aspect = this.vw / this.vh;


    // calculate scene
    const dist = this.camera.position.z - this.particles.position.z;
    const height = 1;
    this.camera.fov = 2 * (180 / Math.PI) * Math.atan(height / (2 * dist));

    this.particles.scale.x = this.aspect * 0.5;
    this.particles.scale.y = 0.5;

    this.camera.updateProjectionMatrix();
  }

  init() {
    // this.container.style.paddingTop = `${this.vh - 150}px`;
    this.renderer.setPixelRatio(this.dpi);
    this.renderer.setSize(this.vw, this.vh);
    this.renderer.setClearColor(0x161616, 0);
    this.element.appendChild(this.renderer.domElement);
    this.scroll();
    // const controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.drawImage();
  }
}



// WEBPACK FOOTER //
// ./scripts/elements/ParticlesPreloader/ParticlesPreloader.js